/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import { useLazyQuery, useMutation } from "@apollo/client";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Image, View } from "react-native";
import Icon from "react-icons-kit";
import { calendar } from "react-icons-kit/entypo/calendar";
import ButtonBase from "@material-ui/core/ButtonBase";
import { share } from "react-icons-kit/entypo/share";
import { arrows_minus } from "react-icons-kit/linea/arrows_minus";
import ReactPlayer from "react-player";
import { Redirect } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import { RootState, SelfObject } from "store/types";

import {
  BackExploreBar,
  Button,
  EditFinishedSummitModal,
  LoadingDots,
  LoadingView,
  Text,
  VideoModal,
  styles,
} from "UpduoLibrary";
import {
  skaleBlack,
  skalePrimaryBlue,
  skaleWebBackgroundGray,
  skaleWebFillLightGray,
  skaleWebTextBackgroundLightGray,
  skaleWhite,
  upduoPrimaryBlue,
  skalecalendarblue,
  upduoPrimaryBlack,
} from "config/UpduoColors";
import GenerateShareLinkModal from "./GenerateShareLinkModal";
import {
  GET_STAFF_SESSION_LIST,
  GET_SUMMIT_ATTENDANCE_LIST,
  GET_SUMMIT_DETAIL,
} from "graphql/internal/Queries";
import { CREATE_OR_GET_SUMMIT_SHARE_LINK } from "graphql/internal/Mutations";
import * as UpduoAnalytics from "config/UpduoAnalytics";
import {
  CreateOrGetSummitShareLinkMutation,
  GetSessionListQuery,
  GetSummitAttendanceListQuery,
  GetSummitDetailQuery,
  SkillModuleType,
  CohortType,
  TimeWindowType,
  SkaleSessionType,
} from "graphql/internal/generated";
import { RecursivePartial } from "config/UtilityTypes";
import EventActivityTable from "./EventActivityTable";
import AttendanceTable from "./AttendanceTable";
import {
  formattedSummitDuration,
  formattedDateAndTimeString,
  getNodes,
  getSummitState,
  SummitState,
  allowedMatchAlgorithmList,
  getResizedImageUrl,
  formattedTimeWindowTime,
  getTimezoneAbbreviation,
  implementTimeZoneOffset,
} from "config/Utilities";
import CsvModal from "../DashboardRoute/TableView/CsvModal";

function SummitDateTimeBlock({ text, summitDateTime }) {
  return (
    <View
      style={{
        flexDirection: "row",
        backgroundColor: skaleWebTextBackgroundLightGray,
        alignItems: "center",
        padding: 10,
        borderRadius: 10,
      }}
    >
      <Icon
        style={{
          paddingBottom: 3,
          color: skalecalendarblue,
        }}
        size={15}
        icon={calendar}
      />
      <Text
        style={{
          marginLeft: 10,
          color: skaleBlack,
          fontSize: 16,
        }}
      >
        {`${text}: ${formattedDateAndTimeString(summitDateTime)}`}
      </Text>
    </View>
  );
}

export default function SummitAdminRoute({ match, history }) {
  const selfObject: SelfObject = useSelector(
    (state: RootState) => state.selfObject
  );

  // The summit detail/editing route component
  const dispatch = useDispatch();
  const summitDescription = useSelector(
    (state: RootState) => state.summitDescription
  );
  // const summitSessionList = useSelector(
  //   (state: RootState) => state.summitSessionList
  // );
  const summitAttendanceList = useSelector(
    (state: RootState) => state.summitAttendanceList
  );
  const summitName = useSelector((state: RootState) => state.summitName);
  // const summitSkillList = useSelector(
  //   (state: typeof RootState) => state.summitSkillList
  // );
  const videoInfo = useSelector((state: RootState) => state.videoInfo);

  const [buttonLoading, setButtonLoading] = useState(false);

  // const [
  //   sessionDetailTableIsLoading,
  //   setSessionDetailTableIsLoading,
  // ] = useState(true);
  const [isPublished, setIsPublished] = useState(false);

  const [summitHasEnded, setSummitHasEnded] = useState<boolean>();
  const [skillModules, setSkillModules] = useState<
    Array<
      RecursivePartial<SkillModuleType> &
        Pick<SkillModuleType, "activeSkillList" | "publishedSkillList">
    >
  >([]);
  const [cohorts, setCohorts] = useState<Array<RecursivePartial<CohortType>>>(
    []
  );

  const [matchAlgorithm, setMatchAlgorithm] = useState<any>(
    allowedMatchAlgorithmList[0]
  );
  const matchAlgorithmRef = useRef<any>();
  matchAlgorithmRef.current = matchAlgorithm;

  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [timeWindows, setTimeWindows] = useState<Array<TimeWindowType>>([]);
  const [generateShareLinkModalVisible, setGenerateShareLinkModalVisible] =
    useState(false);
  const [inviteUuid, setInviteUuid] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState("");
  const [introVideoUrl, setIntroVideoUrl] = useState("");

  const [editFinishedSummitModalVisible, setEditFinishedSummitModalVisible] =
    useState(false);
  const [csvModalVisible, setCsvModalVisible] = useState(false);
  const [eventActivityList, setEventActivityList] = useState<
    SkaleSessionType[]
  >([]);

  const [getSummitDetail, { loading: getSummitDetailLoading }] =
    useLazyQuery<GetSummitDetailQuery>(GET_SUMMIT_DETAIL, {
      fetchPolicy: "network-only", // if this isn't specified, the onCompleted will only run once, since subsequent calls will be cached
      onError: () => history.push("/events"),
      onCompleted: ({ userSummit }) => {
        if (userSummit) {
          setIsPublished(userSummit.isPublished);
          dispatch(ActionCreators.setSummitName(userSummit.name));
          dispatch(ActionCreators.setSummitDescription(userSummit.description));
          setInviteUuid(userSummit.inviteUuid || null);
          const summitStartDate = new Date(Date.parse(userSummit.startDate));
          const summitEndDate = new Date(Date.parse(userSummit.endDate));
          // TODO why are these arrays?
          setStartDate(summitStartDate);
          setEndDate(summitEndDate);
          setSummitHasEnded(
            getSummitState({
              now: Date.now(),
              startDate: summitStartDate,
              endDate: summitEndDate,
            }) === SummitState.COMPLETED
          );
          // TODO remove this any cast after unifying active/published skill list
          setSkillModules(userSummit.skillModules as any);
          setCohorts(userSummit.cohorts);
          const activeSkillList = userSummit.skillModules
            .map((skillModule) => getNodes(skillModule.activeSkillList))
            .flat();
          dispatch(
            ActionCreators.setSummitSkillList(
              // TODO I don't think this works
              // state.summitSkillList is only referenced in one place (in Chat)
              // and it expects a different type (from REST API)
              activeSkillList as any
            )
          );
          setMatchAlgorithm(
            allowedMatchAlgorithmList.find(
              (algorithm) => algorithm.id === userSummit.matchAlgorithm
            )
          );
          setTimeWindows(
            implementTimeZoneOffset(getNodes(userSummit.timeWindows))
          );
          setImageUrl(userSummit.imageUrl!);
          setIntroVideoUrl(userSummit.introVideoUrl!);
        }
      },
    });

  const [
    getEventActivityList,
    { data: activityListData, loading: staffSessionListIsLoading },
  ] = useLazyQuery<GetSessionListQuery>(GET_STAFF_SESSION_LIST, {
    fetchPolicy: "network-only", // if this isn't specified, the onCompleted will only run once, since subsequent calls will be cached
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ staffSessions: data }) => {
      const response = data.edges.map(
        (edge) => edge.node
      ) as SkaleSessionType[];
      setEventActivityList(response);
    },
    onError: (error) => {
      UpduoAnalytics.logEvent("user-summit-geteventactivitylist-error", {
        error: error.message,
      });
      console.error("ERROR getEventActivityList", error);
    },
  });

  const [
    getSummitAttendanceList,
    { data: attendanceData, loading: attendanceTableIsLoading },
  ] = useLazyQuery<GetSummitAttendanceListQuery>(GET_SUMMIT_ATTENDANCE_LIST, {
    fetchPolicy: "network-only", // if this isn't specified, the onCompleted will only run once, since subsequent calls will be cached
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ staffUsers: data }) => {
      let response = data.edges.map((edge) => edge.node);
      if (response.length > 1) {
        response = response.sort((a, b) => {
          // wierd ux here, sorts by session count, if no diff, then sort by name
          let result = b.summitSessionCount - a.summitSessionCount;
          if (result === 0) {
            if (a.firstName > b.firstName) {
              result = 1;
            } else if (a.firstName < b.firstName) {
              result = -1;
            } else {
              result = 0;
            }
          }
          return result;
        });
      }
      dispatch(ActionCreators.setSummitAttendanceList(response));
    },
    onError: (error) => {
      UpduoAnalytics.logEvent("user-summit-getsummitattendancelist-error", {
        error: error.message,
      });
      console.error("ERROR getSummitAttendanceList", error);
    },
  });

  const [createOrGetSummitShareLink, {}] =
    useMutation<CreateOrGetSummitShareLinkMutation>(
      CREATE_OR_GET_SUMMIT_SHARE_LINK,
      {
        onCompleted: ({ createOrGetSummitShareLink: data }) => {
          if (!data.ok) {
            console.error(data.reason);
          }
          setInviteUuid(data.inviteUuid || null);
          setButtonLoading(false);
          setGenerateShareLinkModalVisible(true);
        },
        onError: (err) => {
          console.error(err);
          setButtonLoading(false);
        },
      }
    );

  const _handleCsvModalDismiss = () => {
    setCsvModalVisible(false);
  };

  useEffect(() => {
    _handleClearSummitData();
    const request = { summitId: match.params.id };
    getSummitDetail({
      variables: { getSummitRequest: request },
    });
    // _handleGetSummitSessionList(match.params.id);
    getEventActivityList({
      variables: {
        getStaffSessionListRequest: request,
      },
    });
    getSummitAttendanceList({
      variables: {
        getStaffSummitAttendanceListRequest: request,
      },
    });
    dispatch(ActionCreators.setVideoInfo(null));
  }, [match.params.id]);

  const _handleShareButton = useCallback(() => {
    if (inviteUuid) {
      setGenerateShareLinkModalVisible(true);
    } else {
      setButtonLoading(true);
      createOrGetSummitShareLink({
        variables: {
          createOrGetSummitShareLinkMutationRequest: {
            summitId: match.params.id,
          },
        },
      });
    }
  }, [match.params.id, inviteUuid]);

  if (!selfObject.isStaff) {
    return <Redirect to={"/"} />;
  }

  const _handleClearSummitData = () => {
    dispatch(ActionCreators.setSummitName(""));
    dispatch(ActionCreators.setSummitDescription(""));
    dispatch(ActionCreators.setSummitSkillList([]));
    // dispatch(ActionCreators.setSummitSessionList([]));
    dispatch(ActionCreators.setSummitAttendanceList([]));
  };

  const _handleEditFinishedSummitModalDismiss = () => {
    setEditFinishedSummitModalVisible(false);
    UpduoAnalytics.logEvent("user-edit-finished-summit-modal-back");
  };

  const _handleEditEndedSummit = () => {
    history.push(`/events/detail/edit/${match.params.id}`);
    UpduoAnalytics.logEvent("user-edit-summit-modal-back", {
      summitId: match.params.id,
    });
  };

  const timezone = getTimezoneAbbreviation();

  const loadingDots = (
    <View style={{ alignSelf: "center", marginTop: 20 }}>
      <LoadingDots size={10} />
    </View>
  );

  if (!getSummitDetailLoading) {
    const summitTitle = summitName ? summitName : "Untitled";

    const imageSize = 170;
    const summitDuration = formattedSummitDuration(
      (endDate?.getTime() - startDate?.getTime()) / 1000
    );

    return (
      <View
        style={{
          flex: 1,
          backgroundColor: skaleWebFillLightGray,
          paddingBottom: 50,
        }}
      >
        <BackExploreBar
          previousTitle={"Events"}
          onBack={() => {
            UpduoAnalytics.logEvent("user-summit-detail-back", {
              summitId: match.params.id,
            });
            history.push("/events");
          }}
        />
        <Text
          isHeading={true}
          style={{
            marginTop: 16,
            marginBottom: 4,
            paddingHorizontal: 30,
            color: upduoPrimaryBlue,
            fontSize: 12,
            textTransform: "uppercase",
            fontWeight: "500",
          }}
        >
          {isPublished ? "Published" : "Unpublished"}
        </Text>
        <View
          style={{ flex: 1, paddingTop: 0, paddingHorizontal: 30, zIndex: -95 }}
        >
          <View
            style={{
              zIndex: 5,
              paddingVertical: 10,
              justifyContent: "center",
            }}
          >
            <View
              style={{
                flexDirection: "row",
              }}
            >
              <View style={{ alignSelf: "center", marginHorizontal: 15 }}>
                <Text
                  style={{
                    fontSize: 24,
                    fontWeight: "bold",
                    color: upduoPrimaryBlack,
                  }}
                >
                  {summitTitle}
                </Text>
              </View>
              <View
                style={{
                  flexDirection: "row",
                  marginLeft: 25,
                  paddingTop: 5,
                }}
              >
                <View
                  style={{
                    alignSelf: "center",
                    marginHorizontal: 6,
                  }}
                >
                  {!buttonLoading ? (
                    <ButtonBase onClick={_handleShareButton}>
                      <Icon
                        style={{ color: upduoPrimaryBlue }}
                        size={20}
                        icon={share}
                      ></Icon>
                      <Text
                        isHeading={true}
                        style={{
                          paddingLeft: 3,
                          paddingTop: 3,
                          color: upduoPrimaryBlue,
                          fontSize: 16,
                          fontWeight: "500",
                          textTransform: "uppercase",
                        }}
                      >
                        {"Share"}
                      </Text>
                    </ButtonBase>
                  ) : (
                    <View
                      style={{
                        width: "50px",
                      }}
                    >
                      {loadingDots}
                    </View>
                  )}
                </View>
              </View>
            </View>
          </View>
          <View style={{ alignItems: "center", marginVertical: 15 }}>
            <View style={{ width: "100%" }}>
              <View
                style={{
                  backgroundColor: skaleWhite,
                  borderWidth: 1,
                  borderColor: skaleWebBackgroundGray,
                  padding: 20,
                  borderRadius: 10,
                  marginBottom: 20,
                }}
              >
                <View style={{ alignItems: "flex-end" }}>
                  <Button
                    marginLeft={20}
                    onPress={() => {
                      !summitHasEnded
                        ? _handleEditEndedSummit()
                        : setEditFinishedSummitModalVisible(true);
                    }}
                    width={150}
                    height={36}
                    color={upduoPrimaryBlue}
                    borderColor={skalePrimaryBlue}
                    text={"Edit"}
                  />
                </View>
                <View
                  style={{
                    marginVertical: 32,
                    marginLeft: 78,
                    marginRight: 211,
                  }}
                >
                  <Text style={styles.titleText}>{"Event Name"}</Text>
                  <Text style={styles.descriptionText}>{summitName}</Text>
                  <View style={{ flexDirection: "row" }}>
                    <View style={{ flex: 2 }}>
                      <Text style={styles.webtitleText}>{"Topic(s)"}</Text>
                      <Text style={styles.descriptionText}>
                        {skillModules?.map(
                          (skillModule) => `${skillModule?.name}\n`
                        )}
                      </Text>
                      <Text style={styles.webtitleText}>
                        {"Lessons in this Event:"}
                      </Text>
                      {skillModules
                        .map((skillModule) =>
                          getNodes(skillModule.publishedSkillList)
                        )
                        .flat()
                        .map((item, i) => (
                          <Text style={styles.descriptionText2} key={i}>
                            {`${i + 1}. ${item.name}\n`}
                          </Text>
                        ))}
                    </View>
                    {selfObject.primaryEmail?.endsWith("@upduo.com") ||
                    selfObject.primaryEmail?.endsWith("@arcade.co") ? (
                      <View style={{ flex: 2 }}>
                        <View>
                          <Text style={styles.webtitleText}>
                            {"Match Algorithm"}
                          </Text>
                          <Text style={styles.descriptionText}>
                            {matchAlgorithm?.name}
                          </Text>
                          <Text style={styles.webtitleText}>{"Cohort(s)"}</Text>
                          <Text style={styles.descriptionText}>
                            {cohorts?.map((cohort) => `${cohort?.name}\n`)}
                          </Text>
                        </View>
                      </View>
                    ) : null}
                    <View style={{ flex: 1, minWidth: 180 }}>
                      <Text style={styles.webtitleText}>{"Image"}</Text>
                      {imageUrl ? null : (
                        <Text style={styles.descriptionText}>{"None"}</Text>
                      )}
                      <View
                        style={{
                          marginBottom: 30,
                          width: imageSize,
                          height: imageSize,
                        }}
                      >
                        {imageUrl ? (
                          <Image
                            style={{
                              flex: 1,
                              borderRadius: 6,
                              width: imageSize,
                              height: imageSize,
                            }}
                            source={{
                              uri: getResizedImageUrl(
                                imageUrl,
                                imageSize,
                                imageSize
                              ),
                            }}
                          />
                        ) : null}
                      </View>
                    </View>
                  </View>
                  <Text
                    style={[
                      styles.webtitleText,
                      { marginTop: 40, marginBottom: 20 },
                    ]}
                  >
                    {"Date/Time"}
                  </Text>
                  <View
                    style={{
                      flexDirection: "row",
                      marginBottom: 30,
                    }}
                  >
                    <SummitDateTimeBlock
                      text={"Start"}
                      summitDateTime={startDate}
                    />
                    <Icon
                      style={{
                        alignSelf: "center",
                        paddingLeft: 5,
                        paddingRight: 5,
                      }}
                      size={15}
                      icon={arrows_minus}
                    />
                    <SummitDateTimeBlock
                      text={"End"}
                      summitDateTime={endDate}
                    />
                  </View>
                  <View style={{ marginTop: 48 }}>
                    <Text style={styles.webtitleText}>{"Time Windows"}</Text>
                    {timeWindows.map((timeWindow, index) => {
                      const timeWindowTime =
                        formattedTimeWindowTime(timeWindow);
                      return (
                        <Text style={styles.descriptionText2} key={index}>
                          {`${timeWindow.text}\n${timeWindowTime} ${timezone}\n`}
                        </Text>
                      );
                    })}
                  </View>
                  <Text style={(styles.webtitleText, { marginTop: 48 })}>
                    {"Description"}
                  </Text>
                  <Text style={styles.descriptionText}>
                    {summitDescription}
                  </Text>
                </View>

                <View style={{ marginLeft: 78, marginRight: 46 }}>
                  <View
                    style={{
                      flexDirection: "row",
                    }}
                  >
                    <View
                      style={{
                        flex: 1,
                      }}
                    >
                      <Text style={[styles.titleText, { marginBottom: 16 }]}>
                        {"Intro Video URL"}
                      </Text>
                      {introVideoUrl === "" ? (
                        <Text style={styles.descriptionText}>{"None"}</Text>
                      ) : (
                        <View
                          style={{
                            flex: 1,
                            minWidth: 480,
                          }}
                        >
                          <ReactPlayer
                            controls={true}
                            muted={false}
                            url={introVideoUrl}
                            height={"240px"}
                            width={"426px"}
                          />
                        </View>
                      )}
                    </View>
                  </View>
                </View>
              </View>
              <Text
                isHeading={true}
                style={{ fontSize: 20, width: 220, marginTop: 20 }}
              >
                {"Event Recap"}
              </Text>
              <View>
                <View
                  style={{
                    backgroundColor: skaleWhite,
                    borderWidth: 1,
                    borderColor: skaleWebBackgroundGray,
                    marginVertical: 10,
                    padding: 20,
                    borderRadius: 10,
                  }}
                >
                  <Text isHeading={true} style={{ fontSize: 20 }}>
                    {"Overview"}
                  </Text>
                  <View
                    style={{
                      flexDirection: "row",
                      marginVertical: 10,
                      marginLeft: 50,
                    }}
                  >
                    <Text style={{ fontSize: 16, flex: 1, marginRight: 5 }}>
                      {`Start Date/Time: ${formattedDateAndTimeString(
                        startDate
                      )}\n`}
                      {`End Date/Time: ${formattedDateAndTimeString(
                        endDate
                      )}\n`}
                      {`Event Duration: ${summitDuration}\n`}
                      {attendanceTableIsLoading
                        ? ""
                        : `Participants: ${
                            attendanceData?.staffUsers?.totalCount || 0
                          }`}
                    </Text>
                    {/* <Text style={{ fontSize: 16, flex: 1 }}>
                      {`Lessons: ${
                        summitSessionList.length
                          ? summitSessionList.length
                          : "0"
                      }\n`}
                      {`Total lesson time: ${formattedTotalConversationTimeString(
                        totalSessionDuration
                      )}\n`}
                      {`Average lesson duration: ${formattedFullTimeString(
                        (
                          totalSessionDuration /
                          (summitSessionList.length
                            ? summitSessionList.length
                            : 1)
                        ).toFixed(0)
                      )}\n`}
                      {`Topics prepared: ${
                        summitSkillList.length ? summitSkillList.length : "0"
                      }`}
                    </Text> */}
                  </View>
                </View>
                <View
                  style={{
                    backgroundColor: skaleWhite,
                    borderWidth: 1,
                    borderColor: skaleWebBackgroundGray,
                    marginVertical: 10,
                    padding: 20,
                    borderRadius: 10,
                  }}
                >
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                    }}
                  >
                    <Text isHeading={true} style={{ fontSize: 20 }}>
                      {"Event Activity"}
                    </Text>
                    <Button
                      onPress={() => {
                        setCsvModalVisible(true);
                      }}
                      text={"Export CSV"}
                      height={36}
                      borderColor={skalePrimaryBlue}
                      marginLeft={10}
                    />
                  </View>
                  <View
                    style={{
                      flexDirection: "row",
                      marginVertical: 10,
                      marginHorizontal: 20,
                    }}
                  >
                    {summitAttendanceList.length ? (
                      <EventActivityTable
                        data={eventActivityList}
                        totalCount={
                          activityListData?.staffSessions?.totalCount || 0
                        }
                        fetch={getEventActivityList}
                        pageInfo={activityListData?.staffSessions?.pageInfo}
                        summitId={match.params.id}
                        loading={staffSessionListIsLoading}
                      />
                    ) : (
                      <Text>{"There are no attendees in this community."}</Text>
                    )}
                  </View>
                </View>
                <View
                  style={{
                    backgroundColor: skaleWhite,
                    borderWidth: 1,
                    borderColor: skaleWebBackgroundGray,
                    marginVertical: 10,
                    padding: 20,
                    borderRadius: 10,
                  }}
                >
                  <Text isHeading={true} style={{ fontSize: 20 }}>
                    {attendanceTableIsLoading
                      ? "Members"
                      : `Members (${
                          attendanceData?.staffUsers?.totalCount || 0
                        })`}
                  </Text>
                  <View
                    style={{
                      flexDirection: "row",
                      marginVertical: 10,
                      marginHorizontal: 20,
                    }}
                  >
                    {summitAttendanceList.length ? (
                      <AttendanceTable
                        data={summitAttendanceList}
                        totalCount={attendanceData?.staffUsers?.totalCount || 0}
                        fetch={getSummitAttendanceList}
                        pageInfo={attendanceData?.staffUsers?.pageInfo}
                        summitId={match.params.id}
                        loading={attendanceTableIsLoading}
                      />
                    ) : (
                      <Text>{"There are no attendees in this community."}</Text>
                    )}
                  </View>
                </View>
                {/* <View
                  style={{
                    backgroundColor: skaleWebFillLightGray,
                    marginVertical: 10,
                    padding: 20,
                    borderRadius: 10,
                  }}
                >
                  <Text isHeading={true} style={{ fontSize: 20 }}>
                    {`Lessons (${summitSessionList.length})`}
                  </Text>
                  <View
                    style={{
                      flexDirection: "row",
                      marginVertical: 10,
                      marginHorizontal: 20,
                    }}
                  >
                    {sessionDetailTableIsLoading ? (
                      loadingDots
                    ) : summitSessionList.length > 0 ? (
                      <SessionDetailTable sessionData={summitSessionList} />
                    ) : summitHasEnded ? (
                      <Text>{"There were no sessions for this topic."}</Text>
                    ) : (
                      <Text>
                        {"There are no sessions for this topic yet."}
                      </Text>
                    )}
                  </View>
                </View> */}
              </View>
            </View>
          </View>
        </View>
        <GenerateShareLinkModal
          onClose={() => {
            setGenerateShareLinkModalVisible(false);
          }}
          inviteUuid={inviteUuid}
          isVisible={generateShareLinkModalVisible}
        />
        <VideoModal
          origin={"summit_admin"}
          videoInfo={videoInfo ? videoInfo : null}
          onClose={() => {
            dispatch(ActionCreators.setVideoInfo(null));
          }}
        />
        <EditFinishedSummitModal
          onDismiss={() => _handleEditFinishedSummitModalDismiss()}
          onEditEndedSummitConfirm={() => _handleEditEndedSummit()}
          isVisible={editFinishedSummitModalVisible}
        />
        <CsvModal
          visible={csvModalVisible}
          summitId={match.params.id}
          onClose={_handleCsvModalDismiss}
          exportType={"activity"}
        />
      </View>
    );
  } else {
    return <LoadingView />;
  }
}
